import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getMyShiftSwaps } from '../../../services/shiftSwapService';
import { formatDate, extractListData } from '../../../utils/Utils';
import PersonCell from '../../../components/ui/PersonCell';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'approved' ? '#2e7d3215' :
    $status === 'rejected_peer' || $status === 'rejected_manager' ? '#d32f2f15' :
    $status === 'canceled' ? '#9e9e9e15' :
    '#ed6c0215'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#2e7d32' :
    $status === 'rejected_peer' || $status === 'rejected_manager' ? '#d32f2f' :
    $status === 'canceled' ? '#9e9e9e' :
    '#ed6c02'};
`;

const ShiftLine = styled.div`
  line-height: 1.4;

  &:not(:last-child) {
    margin-bottom: 2px;
  }
`;

const ShiftName = styled.span`
  color: #6b7280;
  margin-left: 6px;
`;

// Task 8'in shiftSwapService.js'i — /shift-swaps/my düz List<ShiftSwapResponse> döner,
// sayfalama YOK (Leave'in server-paginated /leaves/my'ının aksine). Bu yüzden Leave
// deseninin queryParams/URLSearchParams/pageNumber-pageSize plumbing'i burada YOK:
// paramsız tek fetch + refreshTrigger. MyShiftSwapsPage bu tabloyu DataTable'a
// serverSide={false} ile verir (client-side sayfalama/filtre/sıralama).
export const useMyShiftSwapApi = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    setFetching(true);
    getMyShiftSwaps()
      .then((res) => {
        const items = extractListData(res);
        const mapped = items.map((row) => ({
          ...row,
          // Karşı taraf role'e göre değişir (requester ise B'yi, target ise A'yı
          // gösteririz) — DataTable'ın client-side sort/filter'ı gerçek bir data
          // alanı üzerinden çalışsın diye düz bir string olarak ekleniyor.
          counterpartyName: row.role === 'requester' ? row.targetFullName : row.requesterFullName,
          counterpartyProfilePicture: row.role === 'requester' ? row.targetProfilePicture : row.requesterProfilePicture,
          // Tek "not" sütunu için en güncel/ilgili notu seçiyoruz: yönetici kararı
          // varsa o, yoksa akran kararı, yoksa talebin ilk notu. peerAccept bu
          // sayfadan hep boş not gönderdiği için (bkz. MyShiftSwapsPage) kabul
          // sonrası burası boşsa zincir doğal olarak requesterNote'a düşer.
          note: row.managerNote || row.peerDecisionNote || row.requesterNote || '',
        }));
        setData(mapped);
      })
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  const typeLabels = useMemo(() => ({
    trade: t('shiftSwap.type.trade'),
    cover: t('shiftSwap.type.cover'),
  }), [t]);

  const statusLabels = useMemo(() => ({
    pending_peer: t('shiftSwap.status.pending_peer'),
    pending_manager: t('shiftSwap.status.pending_manager'),
    approved: t('shiftSwap.status.approved'),
    rejected_peer: t('shiftSwap.status.rejected_peer'),
    rejected_manager: t('shiftSwap.status.rejected_manager'),
    canceled: t('shiftSwap.status.canceled'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'type',
      label: t('shiftSwap.myList.type'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: { names: ['trade', 'cover'], renderValue: (v) => typeLabels[v] || v },
        customFilterListOptions: { render: (v) => typeLabels[v] || v },
        customBodyRender: (v) => typeLabels[v] || v,
      },
    },
    {
      name: 'counterpartyName',
      label: t('shiftSwap.myList.counterparty'),
      options: {
        filter: true, sort: true, filterType: 'textField',
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.counterpartyProfilePicture} />
        ),
      },
    },
    {
      name: 'requesterShiftDate',
      label: t('shiftSwap.myList.shifts'),
      options: {
        filter: false, sort: true,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return (
            <div>
              <ShiftLine>
                {formatDate(row.requesterShiftDate, lang)}
                {row.requesterShiftName && <ShiftName>{row.requesterShiftName}</ShiftName>}
              </ShiftLine>
              {row.type === 'trade' && row.targetShiftDate && (
                <ShiftLine>
                  {formatDate(row.targetShiftDate, lang)}
                  {row.targetShiftName && <ShiftName>{row.targetShiftName}</ShiftName>}
                </ShiftLine>
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'status',
      label: t('shiftSwap.myList.status'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: ['pending_peer', 'pending_manager', 'approved', 'rejected_peer', 'rejected_manager', 'canceled'],
          renderValue: (v) => statusLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => statusLabels[v] || v },
        customBodyRender: (v) => <StatusBadge $status={v}>{statusLabels[v] || v}</StatusBadge>,
      },
    },
    {
      name: 'note',
      label: t('shiftSwap.myList.note'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v || '—',
      },
    },
  ], [t, data, lang, typeLabels, statusLabels]);

  return { columns, data, fetching, refresh };
};
